import { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { structureItems } from '@/mocks/structure';

export default function StructureSection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const items = el.querySelectorAll('.reveal-item');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const target = entry.target as HTMLElement;
            const index = Array.from(items).indexOf(target);
            target.style.transitionDelay = `${index * 100}ms`;
            target.classList.add('reveal-visible');
            observer.unobserve(target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    items.forEach((item) => observer.observe(item));
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="bg-background-100 py-16 md:py-24">
      <div className="w-full px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <h2 className="reveal-item reveal font-heading text-4xl md:text-6xl lg:text-7xl text-foreground-950 leading-tight">
            {t('home.structure.title')}
            <span className="text-primary-500"> {t('home.structure.titleHighlight')}</span>
          </h2>
          <p className="reveal-item reveal font-body text-base text-foreground-600 mt-4">
            {t('home.structure.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {structureItems.map((item) => (
            <div
              key={item.id}
              className="reveal-item reveal-scale group relative overflow-hidden rounded-lg bg-foreground-950 cursor-pointer transition-transform duration-500 hover:-translate-y-1"
            >
              <div className="aspect-[4/3] w-full overflow-hidden">
                <img
                  src={item.image}
                  alt={t(`structure.${item.id}.title`)}
                  loading="lazy"
                  fetchPriority="low"
                  width="800"
                  height="600"
                  decoding="async"
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-110 transition-all duration-700 ease-out"
                />
              </div>

              <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-foreground-950/15 to-transparent group-hover:from-foreground-950/80 group-hover:via-foreground-950/25 transition-all duration-500"></div>

              <div className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/0 group-hover:bg-background-50/20 backdrop-blur-sm transition-all duration-500 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0">
                <i className="ri-arrow-right-up-line text-background-50 text-lg"></i>
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5 translate-y-1 group-hover:translate-y-0 transition-transform duration-500">
                <h3 className="font-heading text-xl md:text-2xl text-background-50 drop-shadow-sm">
                  {t(`structure.${item.id}.title`)}
                </h3>
                <p className="font-body text-sm text-background-300 mt-1 group-hover:text-background-100 transition-colors duration-500">
                  {t(`structure.${item.id}.description`)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}